import React, { useEffect, useState } from 'react';
import { CalendarDays, Star, Globe, PhoneCall, ArrowRight } from 'lucide-react';
import { WEEK_LABEL, BRAND, CONTACT_EMAIL, PRIVACY_URL, TERMS_URL, HOST_PHOTO_URL, AVATAR_URLS, AVATARS_ILLUSTRATIVE, BADGE_LEFT, HEADER_TITLE, HEADER_LOGO_URL } from './config';

// PALETA BLOQUEADA: vino #9B1B42 · dorado #C9A44C · rosa claro #FBEEF2
const WINE = '#9B1B42';
const GOLD = '#C9A44C';
export function Wrapper({
  children,
  className = ''
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return <div className={`mx-auto w-full max-w-[1080px] px-5 sm:px-8 ${className}`} data-id="element-816" data-mp="4w7e9xqv9mmr">{children}</div>;
}

/* Clases tipográficas compartidas */
export const H1 = 'font-extrabold text-ink tracking-tight leading-[1.08] text-[32px] sm:text-[48px] md:text-[60px] [text-wrap:balance]';
export const H2 = 'text-center font-extrabold text-ink tracking-tight leading-[1.1] text-[28px] sm:text-[40px] md:text-[48px] [text-wrap:balance]';
export const LEAD = 'text-[17px] sm:text-[21px] leading-relaxed text-gray-600';
export const CARD = 'rounded-2xl border border-gray-100 bg-white shadow-[0_10px_30px_-12px_rgba(11,13,18,0.12)]';

/* ---------- Header ---------- */
export function HeaderBar() {
  return <header className="bg-brand" data-id="element-817" data-mp="1b6h6pj7ksdbe">
      <div className="py-3 sm:py-4 flex items-center justify-center gap-2.5 text-white" data-id="element-818" data-mp="1b9lh4ybmw5ai">
        {HEADER_LOGO_URL ? <img src={HEADER_LOGO_URL} alt="logo" height={36} decoding="async" className="h-7 sm:h-9 w-auto" data-id="element-819" data-mp="1789mgedwktvg" /> : <PhoneCall className="w-5 h-5 sm:w-6 sm:h-6" strokeWidth={2} data-id="element-820" data-mp="128nccdtdftqv" />}
        <span className="font-semibold text-[16px] sm:text-[19px] tracking-tight" data-id="element-821" data-mp="xeiz5ctiiphc">{HEADER_TITLE}</span>
      </div>
    </header>;
}

/* ---------- Icons ---------- */
export function TicketIcon({
  className = 'w-14 h-7'
}: {
  className?: string;
}) {
  return <svg viewBox="0 0 120 60" className={className} aria-hidden="true" data-id="element-822" data-mp="jgujqki7sajo">
      <path d="M8 6h104v14a10 10 0 0 0 0 20v14H8V40a10 10 0 0 0 0-20z" fill="#fff" stroke={GOLD} strokeWidth="6" strokeLinejoin="round" data-id="element-823" data-mp="eh9ha03u5k27" />
      <rect x="16" y="14" width="88" height="32" rx="2" fill={GOLD} data-id="element-824" data-mp="4sknqni384cs" />
      <line x1="88" y1="16" x2="88" y2="44" stroke="#fff" strokeWidth="2.5" strokeDasharray="4 3" data-id="element-825" data-mp="8uz8jd5bhe6p" />
      <text x="52" y="36" textAnchor="middle" fontFamily="Inter, Arial, sans-serif" fontWeight="800" fontSize="17" fill="#fff" data-id="element-826" data-mp="3r13trgr5p2vp">1:1</text>
    </svg>;
}
export function GreenCheck({
  className = 'w-7 h-7'
}: {
  className?: string;
}) {
  return <svg viewBox="0 0 24 24" className={className} aria-hidden="true" data-id="element-827" data-mp="nkn9gd9l6atw">
      <circle cx="12" cy="12" r="11" fill={WINE} data-id="element-828" data-mp="sjhpj7g65lab" />
      <path d="M7 12.5l3.2 3.2L17 9" fill="none" stroke="#fff" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" data-id="element-829" data-mp="slawknup27iu" />
    </svg>;
}
export function PendingDot({
  className = 'w-7 h-7'
}: {
  className?: string;
}) {
  return <svg viewBox="0 0 24 24" className={className} aria-hidden="true" data-id="element-830" data-mp="16ihfciff4682">
      <circle cx="12" cy="12" r="10" fill="#fff" stroke={GOLD} strokeWidth="2" strokeDasharray="3 3" data-id="element-831" data-mp="16h698byi2mds" />
      <circle cx="12" cy="12" r="3" fill={GOLD} data-id="element-832" data-mp="1bbiu8qfmk5rn" />
    </svg>;
}
export function VerifiedSeal({
  className = 'w-6 h-6',
  color = WINE
}: {
  className?: string;
  color?: string;
}) {
  return <svg viewBox="0 0 18 18" className={className} aria-hidden="true" data-id="element-833" data-mp="2qz7ehfc85vq5">
      <path fillRule="evenodd" clipRule="evenodd" fill={color} d="M8.03.6c.52-.57 1.42-.57 1.94 0l.91 1c.05.05.12.07.2.05l1.28-.41c.74-.23 1.51.22 1.680.97l.29 1.32c.01.07.07.13.14.14l1.32.29c.75.17 1.2.94.97 1.68l-.41 1.28c-.02.07 0 .15.05.2l1 .91c.57.52.57 1.42 0 1.94l-1 .91c-.05.05-.07.12-.05.2l.41 1.28c.23.74-.22 1.51-.97 1.68l-1.32.29a.18.18 0 0 0-.14.14l-.29 1.32c-.17.75-.94 1.2-1.68.97l-1.28-.41a.18.18 0 0 0-.2.05l-.91 1c-.52.57-1.42.57-1.94 0l-.91-1a.18.18 0 0 0-.2-.05l-1.28.41c-.74.23-1.51-.22-1.68-.97l-.29-1.32a.18.18 0 0 0-.14-.14l-1.32-.29c-.75-.17-1.2-.94-.97-1.68l.41-1.28a.18.18 0 0 0-.05-.2l-1-.91c-.57-.52-.57-1.42 0-1.940l1-.91c.05-.05.07-.12.05-.2l-.41-1.28c-.23-.74.22-1.51.97-1.68l1.32-.29c.07-.01.13-.07.14-.14l.29-1.32c.17-.75.94-1.2 1.68-.97l1.28.41c.07.02.15 0 .2-.05zM13.18 6.98c.2-.31.25-.71.03-1-.27-.37-.6-.63-.9-.8-.4-.23-.89-.05-1.160.33-1.64 2.33-2.67 3.85-2.67 3.85s-.61-.77-1.64-1.87c-.35-.38-.93-.46-1.32-.12-.21.19-.44.41-.65.68-.26.33-.2.8.06 1.13 1.01 1.25 1.99 2.28 2.69 2.97.57.56 1.46.51 1.95-.11.76-.95 1.98-2.57 3.61-5.06z" data-id="element-834" data-mp="34rx1nn3hp6ou" />
    </svg>;
}

// Estrellas: SOLO dentro de testimonios reales
export function Stars({
  size = 18
}: {
  size?: number;
}) {
  return <div className="flex gap-0.5" role="img" aria-label="5 de 5 estrellas" data-id="element-835" data-mp="1ale07np59uli">
      {Array.from({
      length: 5
    }).map((_, i) => <Star key={i} width={size} height={size} className="fill-star text-star" strokeWidth={1} data-id="element-836" data-mp="1fie5hwe66tbk" data-mpi={i} />)}
    </div>;
}
export function VerifiedTag({
  label
}: {
  label: string;
}) {
  return <span className="inline-flex items-center gap-1.5 rounded-full bg-brand-light px-3 py-1 text-[13px] font-semibold text-brand" data-id="element-837" data-mp="1pcmwugrgsg85">
      <VerifiedSeal className="w-4 h-4" data-id="element-838" data-mp="1yayw176ecmc7" />
      {label}
    </span>;
}

/* ---------- Countdown: la agenda de esta semana cierra el domingo 11:59 PM ---------- */
function weekEnd() {
  const now = new Date();
  const end = new Date(now);
  end.setDate(now.getDate() + (7 - now.getDay()) % 7);
  end.setHours(23, 59, 59, 999);
  return end.getTime();
}
function diff() {
  const d = weekEnd() - Date.now();
  if (isNaN(d) || d <= 0) return {
    d: 0,
    h: 0,
    m: 0,
    s: 0
  };
  return {
    d: Math.floor(d / 86400000),
    h: Math.floor(d % 86400000 / 3600000),
    m: Math.floor(d % 3600000 / 60000),
    s: Math.floor(d % 60000 / 1000)
  };
}
export function Countdown() {
  const [t, setT] = useState(diff());
  useEffect(() => {
    const i = setInterval(() => setT(diff()), 1000);
    return () => clearInterval(i);
  }, []);
  const items: [number, string][] = [[t.d, 'Días'], [t.h, 'Horas'], [t.m, 'Min'], [t.s, 'Seg']];
  return <div className="flex items-center justify-center gap-2 sm:gap-3" role="timer" aria-live="off" data-id="element-839" data-mp="p2n8nxxulgw2">
      {items.map(([v, l]) => <div key={l} className="w-[68px] sm:w-[100px] rounded-2xl border border-gray-100 bg-white shadow-[0_8px_24px_-12px_rgba(11,13,18,0.18)] py-3 sm:py-4 flex flex-col items-center" data-id="element-840" data-mp="ka2j58lz5pyc">
          <span className="text-[30px] sm:text-[44px] font-extrabold leading-none text-brand tabular-nums" data-id="element-841" data-mp="2ijsy12me0h12">{String(v).padStart(2, '0')}</span>
          <small className="mt-1.5 text-[11px] sm:text-[13px] font-semibold uppercase tracking-wide text-gray-500" data-id="element-842" data-mp="24rv5a2czgxr2">{l}</small>
        </div>)}
    </div>;
}
export function ScheduleCountdown() {
  return <div className="mx-auto mt-12 max-w-[640px] text-center" data-id="element-843" data-mp="1zul5ptvvr5pi">
      <div className="flex items-center justify-center gap-2 text-brand mb-4" data-id="element-844" data-mp="1zvwc81gpmrkb">
        <CalendarDays className="w-5 h-5 sm:w-6 sm:h-6 shrink-0" strokeWidth={2} data-id="element-845" data-mp="1lw524btbdvbl" />
        <span className="font-bold text-[16px] sm:text-[20px] leading-tight" data-id="element-846" data-mp="1qt50s1nu3vso">La agenda de esta semana cierra en:</span>
      </div>
      <Countdown data-id="element-847" data-mp="3g9e83tmniyp3" />
    </div>;
}

/* ---------- Host row ---------- */
function PhotoPill({
  children,
  className = ''
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return <span className={`absolute z-20 inline-flex items-center gap-1 whitespace-nowrap rounded-full border border-white/60 bg-white/85 px-3.5 py-1.5 text-[13px] font-bold text-ink backdrop-blur-md ${className}`} data-id="element-848" data-mp="3vcnz48ry3upw">
      {children}
    </span>;
}

// Retrato editorial: marco dorado desplazado, fondo rosado difuminado, pastilla y firma
export function HostRow() {
  return <figure className="relative mx-auto mt-14 mb-16 w-full max-w-[280px] sm:max-w-[360px]" data-id="element-849" data-mp="hw6t5cp1aagq">
      <div aria-hidden="true" className="absolute -left-6 -top-6 h-[calc(100%+24px)] w-[calc(100%+24px)] rounded-[40px] bg-gradient-to-br from-brand-light to-brand-line opacity-70 blur-2xl" data-id="element-850" data-mp="cyepxmixv1x8" />
      <div aria-hidden="true" className="absolute inset-0 translate-x-[10px] translate-y-[10px] sm:translate-x-[14px] sm:translate-y-[14px] rounded-[28px] border-[1.5px] border-gold" data-id="element-851" data-mp="mwceyc8mh2qw" />
      <div className="relative z-10 aspect-[4/5] overflow-hidden rounded-[28px] bg-brand-light shadow-[0_30px_60px_-35px_rgba(11,13,18,0.35)]" data-id="element-852" data-mp="2onfpz2mfcjbz">
        {HOST_PHOTO_URL ? <img src={HOST_PHOTO_URL} alt="Chale Rodriguez" width={720} height={900} loading="lazy" decoding="async" className="h-full w-full object-cover object-[50%_18%]" data-id="element-853" data-mp="3d69ukvv4k6fb" /> : <div className="flex h-full w-full items-center justify-center text-xs font-semibold text-brand/70" data-id="element-854" data-mp="3myp9fkb4kevz">[FOTO CHALE]</div>}
      </div>
      <PhotoPill className="left-4 top-4" data-id="element-855" data-mp="2u9rwzxtlhcb5">
        <span className="text-brand" data-id="element-856" data-mp="2ub2wvmjx5zwz">{BADGE_LEFT.value}</span> en {BADGE_LEFT.title}
      </PhotoPill>
      <figcaption className="typ-signature absolute -bottom-7 right-2 sm:-bottom-9 sm:right-3 z-30 -rotate-6 text-[44px] sm:text-[60px] leading-none text-brand" data-id="element-857" data-mp="2z94q30b27fm6">
        Chale
      </figcaption>
    </figure>;
}

/* ---------- Social proof ---------- */
export function TicketLine() {
  return <p className="flex items-center justify-center gap-2.5 text-center text-[16px] sm:text-[22px] font-bold text-brand leading-tight" data-id="element-858" data-mp="ijfq291da712">
      <TicketIcon className="w-9 h-[18px] sm:w-12 sm:h-6 shrink-0" data-id="element-859" data-mp="xmzcirah62ey" />
      <span data-id="element-860" data-mp="xm7gjfcyu2o5">30 minutos, solo tú y Chale</span>
    </p>;
}
const AVATAR_BG = ['#F3D9E1', '#EAD9C8', '#E9CBD5', '#F1E3D6', '#E6C9D2', '#EBDCCB', '#F2D4DE', '#E7D3C1', '#EDCFD9', '#F0DFD0'];

// Sin estrellas aquí: los avatares son de la comunidad, no reseñas
export function TrustStrip({
  withTicket = true
}: {
  withTicket?: boolean;
}) {
  const avatars = AVATAR_URLS.length ? AVATAR_URLS.slice(0, 10) : AVATAR_BG;
  return <div className="flex flex-col items-center gap-4 mt-10 text-center" data-id="element-861" data-mp="eu4ybzkoeebs">
      {withTicket && <TicketLine data-id="element-862" data-mp="9zhdcaovs21y" />}
      <div className="flex justify-center -space-x-2.5" data-id="element-863" data-mp="4y1umpa6e7mq">
        {avatars.map((a, i) => AVATAR_URLS.length ? <img key={i} src={a} alt="" width={44} height={44} loading="lazy" decoding="async" className={`w-9 h-9 sm:w-11 sm:h-11 rounded-full object-cover ring-2 ring-white ${i > 6 ? 'hidden sm:block' : ''}`} data-id="element-864" data-mp="x85jskzyms9" /> : <div key={i} style={{
        background: a
      }} className={`w-9 h-9 sm:w-11 sm:h-11 rounded-full ring-2 ring-white ${i > 6 ? 'hidden sm:block' : ''}`} data-id="element-865" data-mp="1vc2enoepshf8" />)}
      </div>
      <p className="max-w-[320px] sm:max-w-none text-center text-[14px] sm:text-[16px] font-medium text-gray-700 [text-wrap:balance]" data-id="element-866" data-mp="ql99duvlcvnz">
        {!AVATARS_ILLUSTRATIVE && <VerifiedSeal className="mr-1.5 inline w-5 h-5 align-[-4px]" data-id="element-867" data-mp="14i4vanwxyhlg" />}
        Únete a la comunidad que está construyendo algo propio
      </p>
    </div>;
}

/* ---------- CTA ----------
   Botón de marca: degradado vino, filo dorado, brillo que cruza cada pocos segundos,
   flecha en círculo dorado que avanza al pasar el dedo/mouse. Texto siempre centrado. */
export function CtaButton({
  onClick,
  type = 'button',
  form,
  label,
  sub,
  disabled
}: {
  onClick?: () => void;
  type?: 'button' | 'submit';
  form?: string;
  label?: string;
  sub?: string;
  disabled?: boolean;
}) {
  return <button type={type} form={form} onClick={onClick} disabled={disabled} className="cta-btn group relative isolate mx-auto flex w-full max-w-[560px] flex-col items-center justify-center overflow-hidden rounded-full bg-gradient-to-b from-[#B02854] via-brand to-brand-dark px-6 sm:px-10 py-4 sm:py-5 text-center text-white ring-1 ring-inset ring-gold/50 disabled:cursor-not-allowed disabled:opacity-60" data-id="element-868" data-mp="3p8vlz7mhitwx">
      <span aria-hidden="true" className="cta-shine" data-id="element-869" data-mp="1qqtikwjvqr8s" />
      <span className="relative inline-flex items-center justify-center gap-2.5 text-center text-[18px] sm:text-[22px] font-extrabold leading-tight tracking-tight [text-wrap:balance]" data-id="element-870" data-mp="1uvjdoj6ps1sn">
        <span data-id="element-871" data-mp="2njoy14tuvory">{label || 'Reservar mi lugar con Chale'}</span>
        <span className="inline-flex h-7 w-7 sm:h-8 sm:w-8 shrink-0 items-center justify-center rounded-full bg-gold text-brand-dark shadow-[0_4px_10px_-4px_rgba(0,0,0,0.4)] transition-transform duration-200 ease-out group-hover:translate-x-1 group-active:translate-x-1.5" data-id="element-872" data-mp="2nh2ox0rbh17m">
          <ArrowRight className="h-4 w-4" strokeWidth={2.75} aria-hidden="true" data-id="element-873" data-mp="y2nxg8owz08f" />
        </span>
      </span>
      <span className="relative mt-1.5 inline-flex items-center justify-center gap-2 text-[12.5px] sm:text-[14px] font-medium text-white/85 leading-tight" data-id="element-874" data-mp="134n1zlwe436y">
        <span aria-hidden="true" className="cta-dot h-1.5 w-1.5 shrink-0 rounded-full bg-gold" data-id="element-875" data-mp="2zt4tor9eivw3" />
        {sub || 'Cupos limitados esta semana'}
      </span>
    </button>;
}

/* ---------- Aviso de contacto (TCPA / A2P) junto a cualquier envío de teléfono ---------- */
export function ContactConsentNote({
  className = ''
}: {
  className?: string;
}) {
  return <p className={`text-[11.5px] sm:text-[12px] leading-relaxed text-gray-500 ${className}`} data-id="element-876" data-mp="2lznd9fc0hzaa">
      Al enviar, aceptas que {BRAND} y su equipo te contacten por llamada, SMS, WhatsApp o email sobre tu solicitud al número que proporcionaste, incluso con sistemas automatizados. El consentimiento no es condición de compra. La frecuencia de mensajes varía; pueden aplicar tarifas de mensajes y datos. Responde STOP para cancelar o HELP para ayuda. Consulta la{' '}
      <a href={PRIVACY_URL} className="underline underline-offset-2" data-id="element-877" data-mp="6bdj2t9kqhzg">Política de Privacidad</a> y los{' '}
      <a href={TERMS_URL} className="underline underline-offset-2" data-id="element-878" data-mp="1o7z1uvqwonhn">Términos</a>.
    </p>;
}

/* ---------- Footer ---------- */
export function Footer() {
  return <footer className="bg-brand py-12 text-[12px] sm:text-[13px] text-white/85 leading-relaxed" data-id="element-879" data-mp="1xy1uuvy121fz">
      <Wrapper className="max-w-[860px] space-y-4 text-center" data-id="element-880" data-mp="22059ftfztm5h">
        <p className="font-bold text-white" data-id="element-881" data-mp="159mhedujnr6r">Aviso legal y de ingresos</p>
        <p data-id="element-882" data-mp="15bh1jpxzrf9s">{BRAND} es una profesional independiente de servicios financieros. No es empleada ni portavoz de ninguna compañía y este sitio es de su responsabilidad personal. Esto no es una oferta de empleo: es una oportunidad de negocio independiente sujeta a requisitos de licencia, capacitación, verificación de antecedentes y aprobación.</p>
        <p data-id="element-883" data-mp="2ppq0kzmavppu">Los ingresos no están garantizados. Cualquier ejemplo de comisión o ingreso es ilustrativo y no representa resultados típicos; los resultados dependen del esfuerzo, la actividad y las habilidades de cada persona. Las comisiones se pagan por la venta de productos, no por reclutar.</p>
        <p data-id="element-884" data-mp="2o8kfb2vycwyq">La información de este sitio es educativa e informativa. No es una oferta ni una solicitud para vender seguros u otros productos financieros, ni asesoría financiera, legal o fiscal. Los productos de seguros solo son ofrecidos por agentes con licencia vigente en el estado del cliente y están sujetos a aprobación de la aseguradora. No disponible en todos los estados.</p>
        <p data-id="element-885" data-mp="3c3wy19ix9rq6">Este sitio no forma parte del sitio web de Facebook ni de Instagram, ni está respaldado por Meta, Inc., X, TikTok, LinkedIn ni ninguna otra plataforma de redes sociales.</p>
        <p data-id="element-886" data-mp="3ly5kbfhd0ofw">¿Preguntas? Escríbenos a <a className="font-semibold text-white underline underline-offset-2" href={`mailto:${CONTACT_EMAIL}`} data-id="element-887" data-mp="3gx84h9jto6hk">{CONTACT_EMAIL}</a></p>
        <ul className="flex flex-wrap justify-center gap-x-5 gap-y-2 font-medium text-white" data-id="element-888" data-mp="3uxsmi3hwxya0">
          <li data-id="element-889" data-mp="3pypi8l9rbw33"><a href={PRIVACY_URL} className="underline-offset-2 hover:underline" data-id="element-890" data-mp="3pypgogu3zo0z">Política de Privacidad</a></li>
          <li data-id="element-891" data-mp="dfdbqsywi5up"><a href={TERMS_URL} className="underline-offset-2 hover:underline" data-id="element-892" data-mp="delfrh1e663w">Términos y Condiciones</a></li>
        </ul>
        <p className="text-white/70" data-id="element-893" data-mp="ib2f77e1k6wa">© 2026 {BRAND}. Todos los derechos reservados.</p>
      </Wrapper>
    </footer>;
}
export { Globe, WEEK_LABEL };